import { supabase } from '../supabaseClient';
import { formatSafariDate } from './textUtils';


export interface ChatMessage {
    id: string; // UUID from DB or temp ID
    role: 'user' | 'assistant';
    content: string;
    createdAt: Date;
}

export interface ChatSession {
    id: string;
    title: string;
    updatedAt: Date;
}

/**
 * Creates a new chat session in Supabase
 */
export const createSession = async (userId: string, firstMessage?: string): Promise<string | null> => {
    try {
        const title = firstMessage ? firstMessage.substring(0, 30) + (firstMessage.length > 30 ? '...' : '') : '새로운 상담';
        const { data, error } = await supabase
            .from('chat_sessions')
            .insert({ user_id: userId, title })
            .select('id')
            .single();

        if (error) throw error;
        return data.id;
    } catch (error) {
        console.error("Failed to create session", error);
        return null;
    }
};

/**
 * Loads messages for a session
 */
export const loadMessages = async (sessionId: string): Promise<ChatMessage[]> => {
    try {
        const { data, error } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('session_id', sessionId)
            .order('created_at', { ascending: true });

        if (error) throw error;

        return data.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            createdAt: new Date(formatSafariDate(msg.created_at))
        }));
    } catch (error) {
        console.error("Failed to load messages", error);
        return [];
    }
};

/**
 * Sends a message to Gemini and handles streaming response
 */
export const sendMessage = async (
    sessionId: string,
    userMessage: string,
    history: ChatMessage[],
    userContext: any,
    onToken?: (token: string) => void
): Promise<string> => {
    try {
        let pastContext = '';
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            const { data: sessions } = await supabase.from('chat_sessions')
                .select('id').eq('user_id', user.id).neq('id', sessionId)
                .order('created_at', { ascending: false }).limit(3);

            if (sessions && sessions.length > 0) {
                const sessionIds = sessions.map(s => s.id);
                const { data: msgs } = await supabase.from('chat_messages')
                    .select('*').in('session_id', sessionIds)
                    .order('created_at', { ascending: false }).limit(5);
                if (msgs && msgs.length > 0) {
                    pastContext = msgs.reverse().map(m => `${m.role === 'user' ? '사용자' : '신령님'}: ${m.content}`).join('\n');
                }
            }
        }

        // 2. Call Backend API with Auth Token (서버가 sessionId 및 DB 기록 기준으로 과금 턴 자동 판정)
        const { data: { session: activeSession } } = await supabase.auth.getSession();
        const authToken = activeSession?.access_token;

        const response = await fetch('/api/chat', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
            },
            body: JSON.stringify({
                sessionId,
                message: userMessage,
                mbti: userContext?.mbti,
                birthDate: userContext?.birthDate,
                birthTime: userContext?.birthTime,
                name: userContext?.name,
                gender: userContext?.gender,
                pastContext: pastContext,
                messages: history
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`API Error: ${response.status} - ${errorData.error || errorData.details || 'Unknown error'}`);
        }

        // 3. Handle Streaming Response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullText = '';

        if (!reader) {
            throw new Error("Failed to get reader from response");
        }

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            fullText += chunk;
            if (onToken) onToken(chunk);
        }

        // 4. Save Final Bot Response to DB
        await supabase.from('chat_messages').insert({
            session_id: sessionId,
            role: 'assistant',
            content: fullText
        });

        // Update session timestamp
        await supabase.from('chat_sessions').update({ updated_at: new Date() }).eq('id', sessionId);

        return fullText;

    } catch (error) {
        console.error("Chat Service Error:", error);
        throw error; // Let the caller handle it for credit protection
    }
};

/**
 * Generates specific analysis (Job, Compatibility) using Gemini
 */
export const getDetailedAnalysis = async (
    type: 'job' | 'compatibility',
    userProfile: any,
    partnerProfile?: any,
    session?: any
): Promise<any> => {
    // 1. Prepare Prompts based on Type (Logic moved to Backend)
    // We just pass the profile data to the API as is.


    // 2. Call Backend API for Analysis
    try {
        let currentSession = session;
        if (!currentSession) {
            const { data: { session: fetchedSession } } = await supabase.auth.getSession();
            currentSession = fetchedSession;
        }
        const token = currentSession?.access_token;
        const url = type === 'job' ? '/api/job' : '/api/compatibility';
        const bodyPayload = type === 'job'
            ? { ...userProfile }
            : { myProfile: userProfile, partnerProfile, relationshipType: partnerProfile?.relationshipType || 'lover' };

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(bodyPayload)
        });

        if (!response.ok) {
            const errBase = await response.json().catch(() => ({}));
            const detailMsg = errBase.details ? ` (${errBase.details})` : '';
            throw new Error(`Analysis API Error: ${errBase.error || response.statusText}${detailMsg}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Analysis Service Error", error);
        throw new Error("심층 분석 중 오류가 발생했습니다.");
    }
};
